/**
 * Cliente HTTP PÚBLICO — sem interceptor de auth (Bearer).
 * Usado pela página de conferência de fechamento acessada via magic-link.
 */
import axios, { type AxiosInstance } from "axios";

const baseURL = import.meta.env.VITE_API_URL ?? "/api";

const publicApi: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

export interface PublicClosingItem {
  id: string;
  job_id: string;
  job_code: string;
  description: string;
  amount: string;
  scheduled_at: string | null;
  recognized: boolean;
  dispute_reason: string | null;
}

export interface PublicClosing {
  id: string;
  client_name: string | null;
  year: number;
  month: number;
  status: string;
  gross_total: string;
  recognized_total: string;
  disputed_total: string;
  item_count: number;
  closed_by_name: string | null;
  items: PublicClosingItem[];
}

// ─── NF do técnico (magic-link) ─────────────────────────────────────────────
export interface PublicNfItem {
  job_code: string | null;
  description: string | null;
  service_amount: string;
  reimbursement_amount: string;
  total: string;
}

export interface PublicNfInvoice {
  id: string;
  code: string;
  status: string;
  period_year: number | null;
  period_month: number | null;
  nf_deadline: string | null;
  services_total: string;
  reimbursement_total: string;
  total: string;
  nf_filename: string | null;
  nf_submitted_at: string | null;
  nerd_name: string | null;
  items: PublicNfItem[];
}

export const publicNfApi = {
  get: async (token: string): Promise<PublicNfInvoice> => {
    const { data } = await publicApi.get<PublicNfInvoice>(`/v1/public/nf/${token}`);
    return data;
  },
  uploadUrl: async (
    token: string,
    filename: string,
    content_type = "application/pdf",
  ): Promise<{ upload_url: string; storage_path: string }> => {
    const { data } = await publicApi.post(`/v1/public/nf/${token}/upload-url`, {
      filename,
      content_type,
    });
    return data;
  },
  putFile: async (uploadUrl: string, file: File): Promise<void> => {
    // PUT direto no GCS (signed URL) — sem Authorization, com Content-Type.
    await axios.put(uploadUrl, file, { headers: { "Content-Type": file.type || "application/pdf" } });
  },
  submit: async (token: string, filename: string, storage_path: string): Promise<PublicNfInvoice> => {
    const { data } = await publicApi.post<PublicNfInvoice>(`/v1/public/nf/${token}/submit`, {
      filename,
      storage_path,
    });
    return data;
  },
};

export const publicClosingApi = {
  get: async (token: string): Promise<PublicClosing> => {
    const { data } = await publicApi.get<PublicClosing>(`/v1/public/closings/${token}`);
    return data;
  },
  review: async (
    token: string,
    disputed: { item_id: string; reason: string | null }[],
  ): Promise<PublicClosing> => {
    const { data } = await publicApi.post<PublicClosing>(
      `/v1/public/closings/${token}/review`,
      { disputed },
    );
    return data;
  },
  confirm: async (token: string, name: string | null): Promise<PublicClosing> => {
    const { data } = await publicApi.post<PublicClosing>(
      `/v1/public/closings/${token}/confirm`,
      { name },
    );
    return data;
  },
};
